
import { Point3D, Orientation } from '../types';
import { project } from '../utils/isometricMath';
import React, { memo, useMemo } from 'react';

// Define the interface for cube neighbors to prevent type inference errors with default values in destructuring
interface CubeNeighbors {
  top?: boolean;
  bottom?: boolean;
  front?: boolean;
  back?: boolean;
  left?: boolean;
  right?: boolean;
}

interface CubeRendererProps {
  position: Point3D;
  rotation: { x: number, y: number };
  color: string;
  transparent?: boolean;
  onFaceClick?: (pos: Point3D, face: Orientation) => void;
  onFaceHover?: (pos: Point3D | null, face?: Orientation) => void;
  isGhost?: boolean;
  isValid?: boolean;
  // Diccionario de vecinos para ocultar caras compartidas
  neighbors?: CubeNeighbors;
}

export const CubeRenderer: React.FC<CubeRendererProps> = memo(({ 
  position, rotation, color, transparent, onFaceClick, onFaceHover, isGhost, isValid = true, neighbors = {} as CubeNeighbors
}) => {
  const getP = (dx: number, dy: number, dz: number) => 
    project({ x: position.x + dx, y: position.y + dy, z: position.z + dz }, rotation);

  // Vértices del cubo (X: horizontal, Y: altura, Z: profundidad)
  const v = useMemo(() => ({
    v000: getP(0, 0, 0), v100: getP(1, 0, 0),
    v110: getP(1, 1, 0), v010: getP(0, 1, 0),
    v001: getP(0, 0, 1), v101: getP(1, 0, 1),
    v111: getP(1, 1, 1), v011: getP(0, 1, 1),
  }), [position, rotation]);

  const faces = useMemo(() => {
    const { v000, v100, v110, v010, v001, v101, v111, v011 } = v;
    
    const ry = (rotation.y * Math.PI) / 180;
    const rx = (rotation.x * Math.PI) / 180;

    const getFaceDepth = (p: Point3D) => {
      const z1 = -p.x * Math.sin(ry) + p.z * Math.cos(ry);
      return p.y * Math.sin(rx) + z1 * Math.cos(rx);
    };

    const faceDefinitions = [
      { id: 'top', pts: [v010, v110, v111, v011], orient: 'top' as Orientation, brightness: 1.05, center: { x: 0.5, y: 1, z: 0.5 }, active: !neighbors.top },
      { id: 'bottom', pts: [v000, v100, v101, v001], orient: 'top' as Orientation, brightness: 0.4, center: { x: 0.5, y: 0, z: 0.5 }, active: !neighbors.bottom },
      { id: 'front', pts: [v000, v100, v110, v010], orient: 'right' as Orientation, brightness: 0.85, center: { x: 0.5, y: 0.5, z: 0 }, active: !neighbors.back }, // Mapped to our Z-axis logic
      { id: 'back', pts: [v001, v101, v111, v011], orient: 'left' as Orientation, brightness: 0.65, center: { x: 0.5, y: 0.5, z: 1 }, active: !neighbors.front },
      { id: 'right', pts: [v100, v101, v111, v110], orient: 'right' as Orientation, brightness: 0.95, center: { x: 1, y: 0.5, z: 0.5 }, active: !neighbors.right },
      { id: 'left', pts: [v000, v001, v011, v010], orient: 'left' as Orientation, brightness: 0.75, center: { x: 0, y: 0.5, z: 0.5 }, active: !neighbors.left },
    ];

    return faceDefinitions
      .filter(f => f.active) // Solo renderizar caras que no toquen otro cubo
      .map(f => ({ ...f, depth: getFaceDepth({ 
        x: position.x + f.center.x, 
        y: position.y + f.center.y, 
        z: position.z + f.center.z 
      }) }))
      .sort((a, b) => b.depth - a.depth);
  }, [v, rotation, position, neighbors]);

  const opacity = transparent ? 0.4 : isGhost ? 0.5 : 1.0;
  // El trazo ahora es del mismo color que el relleno para evitar huecos de anti-aliasing
  const getStroke = (b: number) => isGhost ? (isValid ? '#22c55e' : '#ef4444') : 'none';
  const fillColor = isGhost && !isValid ? '#fee2e2' : color;

  return (
    <g style={{ pointerEvents: isGhost ? 'none' : 'auto' }}>
      {faces.map(face => (
        <path 
          key={face.id}
          d={`M ${face.pts[0].x} ${face.pts[0].y} L ${face.pts[1].x} ${face.pts[1].y} L ${face.pts[2].x} ${face.pts[2].y} L ${face.pts[3].x} ${face.pts[3].y} Z`}
          fill={fillColor}
          stroke={getStroke(face.brightness)}
          strokeWidth={isGhost ? "1" : "0.5"} // Pequeño solapamiento para evitar líneas blancas entre caras
          style={{ 
            fillOpacity: opacity, 
            filter: face.brightness !== 1 ? `brightness(${face.brightness})` : 'none',
            cursor: 'pointer'
          }}
          onClick={(e) => { e.stopPropagation(); onFaceClick?.(position, face.orient); }}
          onMouseEnter={() => !isGhost && onFaceHover?.(position, face.orient)}
          onMouseLeave={() => !isGhost && onFaceHover?.(null)}
        />
      ))}
    </g>
  );
});
